import { useMemo } from "react";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import CircularArrowBackIcon from "../../../../../../../assets/svg/circular-arrow-back-icon";
import PlusIcon from "../../../../../../../assets/svg/plus-icon";
import SettingsIcon from "../../../../../../../assets/svg/settings-icon";
import QuestionandanswerIcon from "../../../../../../../assets/svg/questionandanswer-icon";
import {
  FilterGroupUI,
  FilterFieldOption,
  createEmptyGroup,
  getOperatorsForFilterFieldKey,
  FILTER_FIELD_OPTIONS,
} from "../utils/constants";
import { FilterGroupBlock } from "./FilterGroupBlock";
import { FilterDropdown } from "./FilterConditionRow";
import { useGetHelpdeskCustomFieldsQuery } from "../../../../../../../redux/services/helpdeskApi";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { getClientDomain } from "../../../../../../../utils/functions/client-code";
import { isHelpdeskResolutionClient } from "../../../utils/function";
import ChevronIcon from "../../../../../../../assets/svg/chevron-icon";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import Button from "../../../../../../../components/Button/Button";
import {
  FIELD_OPTIONS,
  UI_TO_API_FIELD_TYPE,
} from "../../CreateTicket/utils/constants";
import type { CustomFieldType } from "../../CreateTicket/utils/types";

const CUSTOM_FIELD_TYPE_ICON_MAP: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; className: string }
> = Object.fromEntries(
  FIELD_OPTIONS.map((opt) => [
    UI_TO_API_FIELD_TYPE[opt.id as CustomFieldType],
    { icon: opt.icon, className: opt.iconClass },
  ])
);

const CUSTOM_FIELD_KEY_ICON_MAP: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; className?: string }
> = {
  "system-name": { icon: SettingsIcon },
  "issue-type": { icon: QuestionandanswerIcon },
};

interface CreateViewFiltersProps {
  filterGroups: FilterGroupUI[];
  logicalOperator: "and" | "or";
  onFilterGroupsChange: (groups: FilterGroupUI[]) => void;
  onLogicalOperatorChange: (op: "and" | "or") => void;
  onBack: () => void;
  onClose: () => void;
  onSave: () => void;
  isEditMode?: boolean;
  isSaveViewLoading?: boolean;
  hasUnsavedChanges?: boolean;
}

const AddFilterDropdown = ({
  onSelect,
  options,
}: {
  onSelect: (fieldKey: string) => void;
  options: FilterFieldOption[];
}) => {
  return (
    <FilterDropdown
      options={options.map((f) => ({ key: f.key, label: f.label }))}
      onChange={onSelect}
      width="w-auto"
      dropdownClassName="!min-w-[180px] !max-w-[200px] !max-h-[400px] !left-0 !top-[55px]"
      customTrigger={() => (
        <div className="bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk rounded-full w-[60px] h-[48px] flex items-center justify-center shadow-card border-[2.5px] border-border-gray dark:border-border-gray-dk hover:scale-105 transition-transform">
          <PlusIcon
            className="w-5 h-5 text-pure-white dark:text-pure-white-dk"
            strokeWidth={1.1}
            stroke="currentColor"
          />
        </div>
      )}
      renderOption={(opt) => {
        const field = options.find((f) => f.key === opt.key);
        if (!field) return opt.label;
        const Icon = field.icon;
        return (
          <>
            <Icon
              className={`shrink-0 ${field.iconClassName || "w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"}`}
            />
            <TruncatedTextTooltip text={field.label} maxWidth="max-w-[120px]" />
          </>
        );
      }}
    />
  );
};

const TopLevelLogicalToggle = ({
  value,
  onChange,
  disabled = false,
}: {
  value: "and" | "or";
  onChange: (val: "and" | "or") => void;
  disabled?: boolean;
}) => {
  if (disabled) {
    return (
      <span className="px-2 py-1 text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk uppercase select-none">
        {value?.toUpperCase()}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onChange(value === "and" ? "or" : "and")}
      className="flex items-center gap-0.5 px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded-md text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer transition-colors uppercase"
    >
      {value?.toUpperCase()}
      <ChevronIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
    </button>
  );
};

export const CreateViewFilters = ({
  filterGroups,
  logicalOperator,
  onFilterGroupsChange,
  onLogicalOperatorChange,
  onBack,
  onClose,
  onSave,
  isEditMode = false,
  isSaveViewLoading = false,
  hasUnsavedChanges = true,
}: CreateViewFiltersProps) => {
  const hasFilters = filterGroups?.length > 0;

  const hasValidFilters = useMemo(
    () =>
      filterGroups?.some((g) =>
        g?.conditions?.some((c) => c?.key && c?.value?.length > 0)
      ) ?? false,
    [filterGroups]
  );

  const handleFooterCancel = () => {
    if (isEditMode) {
      onClose();
    } else {
      onBack();
    }
  };

  const { data: customFieldsData } = useGetHelpdeskCustomFieldsQuery({});

  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());

  const baseFilterFieldOptions = useMemo(() => {
    if (isResolutionEnabled) return FILTER_FIELD_OPTIONS;
    return FILTER_FIELD_OPTIONS?.filter((f) => f?.key !== "resolution_reason");
  }, [isResolutionEnabled]);

  const allFilterFieldOptions = useMemo(() => {
    const customFields = (
      customFieldsData as HelpdeskCustomFieldDefinition[] | undefined
    )?.filter((f) => f?.is_active && f?.show_in_filter);

    if (!customFields?.length) return baseFilterFieldOptions;

    const staticKeys = new Set(baseFilterFieldOptions?.map((f) => f.key));
    const PRIORITY_KEYS = ["system-name", "issue-type"];
    const customOptions: FilterFieldOption[] = customFields
      .filter((f) => !staticKeys.has(f.key))
      .map((f) => {
        const keyIcon = CUSTOM_FIELD_KEY_ICON_MAP[f.key];
        const iconEntry = keyIcon ?? CUSTOM_FIELD_TYPE_ICON_MAP[f.type];
        return {
          key: f.key,
          label: f.name,
          icon: iconEntry?.icon ?? SettingsIcon,
          iconClassName: iconEntry?.className,
        };
      })
      .sort((a, b) => {
        const aIdx = PRIORITY_KEYS.indexOf(a.key);
        const bIdx = PRIORITY_KEYS.indexOf(b.key);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return 0;
      });

    return [...baseFilterFieldOptions, ...customOptions];
  }, [customFieldsData, baseFilterFieldOptions]);

  const handleAddFilterFromEmpty = (fieldKey: string) => {
    const isCustom = !FILTER_FIELD_OPTIONS?.some((f) => f?.key === fieldKey);
    const customFieldType = (
      customFieldsData as HelpdeskCustomFieldDefinition[] | undefined
    )?.find((f) => f?.key === fieldKey)?.type;
    const newGroup = createEmptyGroup();
    newGroup.conditions[0].key = fieldKey;
    newGroup.conditions[0].is_custom_field = isCustom;
    newGroup.conditions[0].comparison_operator = getOperatorsForFilterFieldKey(
      fieldKey,
      customFieldType
    )[0]?.key;
    onFilterGroupsChange([...filterGroups, newGroup]);
  };

  const handleAddFilter = () => {
    onFilterGroupsChange([...filterGroups, createEmptyGroup()]);
  };

  const handleGroupChange = (idx: number, updated: FilterGroupUI) => {
    const newGroups = [...filterGroups];
    newGroups[idx] = updated;
    onFilterGroupsChange(newGroups);
  };

  const handleDeleteGroup = (idx: number) => {
    onFilterGroupsChange(filterGroups?.filter((_, i) => i !== idx));
  };

  const handleClearAll = () => {
    onFilterGroupsChange([]);
  };

  return (
    <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk overflow-hidden rounded-l-[8px]">
      <div className="border-b border-border-gray dark:border-border-gray-dk flex items-center justify-between px-3 h-[50px] shrink-0 rounded-tl-[8px]">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
            aria-label="Back to view details"
          >
            <CircularArrowBackIcon className="w-5 h-5 text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
          <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            Filters
          </span>
        </div>
        <div className="flex items-center gap-4">
          {hasFilters && (
            <button
              onClick={handleClearAll}
              className="h-[26px] px-3 flex items-center justify-center border border-verba-red-alert dark:border-verba-red-alert-dk rounded-md text-xs font-medium text-verba-red-alert dark:text-verba-red-alert-dk hover:bg-verba-red-alert dark:hover:bg-verba-red-alert-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-colors cursor-pointer"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="w-[18px] h-[18px] bg-tag-gray dark:bg-tag-gray-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
            aria-label="Close"
          >
            <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-y-auto">
        {!hasFilters ? (
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-6 px-4">
            <AddFilterDropdown
              onSelect={handleAddFilterFromEmpty}
              options={allFilterFieldOptions}
            />
            <span className="text-lg text-verba-indigo dark:text-verba-indigo-dk">
              Click to add a "Filter"
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-4 py-6">
            {filterGroups?.map((group, gIdx) => (
              <div
                key={gIdx}
                className={`flex ${group?.conditions?.length > 1 ? "items-center" : "items-start"}`}
              >
                {gIdx > 0 && (
                  <div
                    className={`${group?.conditions?.length > 1 ? "" : "pt-3"} w-[70px] shrink-0`}
                  >
                    <TopLevelLogicalToggle
                      value={logicalOperator}
                      onChange={onLogicalOperatorChange}
                      disabled={gIdx > 1}
                    />
                  </div>
                )}
                <FilterGroupBlock
                  group={group}
                  onGroupChange={(updated) => handleGroupChange(gIdx, updated)}
                  onDeleteGroup={() => handleDeleteGroup(gIdx)}
                  isFirstGroup={gIdx === 0}
                  fieldOptions={allFilterFieldOptions}
                  customFieldDefinitions={
                    customFieldsData as
                      | HelpdeskCustomFieldDefinition[]
                      | undefined
                  }
                />
              </div>
            ))}

            <div className="pl-[70px] pt-3">
              <button
                type="button"
                onClick={handleAddFilter}
                className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] px-10 flex items-center gap-1 cursor-pointer"
              >
                <PlusIcon className="w-3 h-3 text-verba-blue dark:text-verba-blue-dk" />
                <span className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk">
                  Add Filter
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="px-[18px] py-[18px] backdrop-blur-[20px] shrink-0 border-t border-border-gray dark:border-border-gray-dk rounded-bl-[8px]">
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            onClick={handleFooterCancel}
            innerText="Cancel"
            disabled={isSaveViewLoading}
            buttonContainerClass="h-[26px] w-[100px] shrink-0 border border-border-gray dark:border-border-gray-dk rounded-md text-md font-semibold text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors bg-transparent cursor-pointer"
            innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
          />
          <Button
            type="button"
            onClick={onSave}
            innerText={isSaveViewLoading ? "Saving..." : "Save View"}
            disabled={
              !hasValidFilters || isSaveViewLoading || !hasUnsavedChanges
            }
            buttonContainerClass="h-[26px] px-12 shrink-0 bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md enabled:cursor-pointer disabled:!cursor-not-allowed transition-opacity hover:enabled:opacity-90 disabled:opacity-50 disabled:hover:opacity-50"
            innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </div>
    </div>
  );
};
